import { useState } from 'react';

export default function BeachClubLaurel() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const images = [
    {
      url: "https://i.imgur.com/yQH9c3H.jpg",
      alt: "Beach Club Laurel - Vista panorámica"
    },
    {
      url: "https://i.imgur.com/SA9sUay.jpg",
      alt: "Beach Club Laurel - Piscina"
    },
    {
      url: "https://i.imgur.com/L2mcUHn.jpg",
      alt: "Beach Club Laurel - Zona de relax"
    },
    {
      url: "https://i.imgur.com/dx2dTVz.jpg",
      alt: "Beach Club Laurel - Instalaciones"
    }
  ];

  return (
    <>
      <section id="beach-club" className="py-32 px-6 bg-gradient-to-br from-stone-50 via-white to-stone-50 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/30 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/30 rounded-full blur-3xl -z-10"></div>
        
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="flex items-center gap-3 bg-stone-800 text-white px-6 py-2 rounded-full">
                <i className="ri-water-flash-line text-2xl"></i>
                <span className="text-sm font-medium uppercase tracking-wider">Instalacja Premium</span>
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-stone-800 mb-6">
              Beach Club Laurel
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-stone-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              Jedna z pereł Hotel Jardín Tecina. Ekskluzywna przestrzeń relaksu i rekreacji 
              przy morzu z basenem, solarium i spektakularnymi widokami na Atlantyk.
            </p>
          </div>

          {/* Important Notice - Temporary Closure */}
          <div className="mb-12 bg-amber-50 border-2 border-amber-300 rounded-xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                <i className="ri-information-line text-amber-800 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-3">Ważne: Tymczasowe zamknięcie</h3>
                <p className="text-amber-800 leading-relaxed mb-2">
                  <strong>Beach Club Laurel będzie tymczasowo zamknięty od 13 kwietnia do 30 czerwca 2026 roku</strong> w celu przeprowadzenia prac konserwacyjnych i ulepszeń.
                </p>
                <p className="text-amber-700 text-sm">
                  Więcej informacji: <a href="https://www.jardin-tecina.com/en/beach-club-laurel/temporary-closure/" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">jardin-tecina.com/beach-club-laurel/temporary-closure</a>
                </p>
              </div>
            </div>
          </div>

          {/* Photo Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {/* Large featured image */}
            <div 
              className="md:row-span-2 relative group overflow-hidden rounded-2xl shadow-2xl cursor-pointer"
              onClick={() => setSelectedImage(0)}
            >
              <div className="w-full h-[600px]">
                <img 
                  src={images[0].url}
                  alt={images[0].alt}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-6 left-6 text-white">
                  <i className="ri-zoom-in-line text-3xl"></i>
                </div>
              </div>
            </div>

            {/* Grid of smaller images */}
            {images.slice(1).map((image, index) => (
              <div 
                key={index + 1}
                className="relative group overflow-hidden rounded-2xl shadow-xl cursor-pointer"
                onClick={() => setSelectedImage(index + 1)}
              >
                <div className="w-full h-[290px]">
                  <img 
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-5">
                <i className="ri-swimming-pool-line text-stone-700 text-3xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-stone-800 mb-3">Basen z widokiem</h4>
              <p className="text-stone-600 leading-relaxed">
                Basen z panoramicznym widokiem na ocean Atlantycki. 
                Wyjątkowa przestrzeń do cieszenia się subtropikalnym klimatem La Gomery.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-5">
                <i className="ri-sun-line text-stone-700 text-3xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-stone-800 mb-3">Solarium Premium</h4>
              <p className="text-stone-600 leading-relaxed">
                Rozległe strefy solarium z luksusowymi leżakami, parasolami i serwisem ręczników. 
                Idealne miejsce na absolutny relaks pod kanaryjskim słońcem.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-5">
                <i className="ri-restaurant-2-line text-stone-700 text-3xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-stone-800 mb-3">Bar i Restauracja</h4>
              <p className="text-stone-600 leading-relaxed mb-4">
                Obsługa baru i restauracji przy basenie. Kuchnia śródziemnomorska, 
                orzeźwiające koktajle i przekąski przez cały dzień.
              </p>
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="text-sm text-stone-600 leading-relaxed mb-2">
                  <strong>Restauracja Trattoría:</strong> Również dostępna na kolacje (à la carte lub menu degustacyjne). Kuchnia śródziemnomorska.
                </p>
                <p className="text-sm text-stone-600 leading-relaxed mb-2">
                  <strong>Restauracja El Laurel:</strong> Kuchnia Gourmet. Kolacje à la carte lub menu degustacyjne.
                </p>
                <p className="text-xs text-stone-500 italic">
                  Obie restauracje dostępne w zależności od dostępności i po wcześniejszej rezerwacji.
                </p>
              </div>
            </div>
          </div>

          {/* Highlights */}
          <div className="bg-gradient-to-r from-stone-800 to-stone-700 rounded-2xl p-12 text-white shadow-2xl">
            <h3 className="text-3xl font-light mb-8 text-center">
              Dlaczego Club Laurel jest wyjątkowy?
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold mb-2 text-lg">Wyjątkowe położenie nad oceanem</h5>
                  <p className="text-stone-200 leading-relaxed">
                    Miejsce z spektakularnymi widokami, gdzie towarzyszy Ci dźwięk morza.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold mb-2 text-lg">Day-pass także dla osób spoza hotelu</h5>
                  <p className="text-stone-200 leading-relaxed">
                    Club Laurel jest otwarty również dla gości zewnętrznych (w określonych godzinach) – dostępność warto potwierdzić z wyprzedzeniem.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold mb-2 text-lg">Basen z podgrzewaną wodą morską</h5>
                  <p className="text-stone-200 leading-relaxed">
                    Możliwość kąpieli w basenie z wodą morską (klimatyzowany/podgrzewany) w wyjątkowej scenerii.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold mb-2 text-lg">Gastronomia i wyjątkowe doświadczenia</h5>
                  <p className="text-stone-200 leading-relaxed">
                    Restauracje na miejscu + propozycje typu &quot;Cueva para dos&quot; oraz brunch.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 md:col-span-2 md:max-w-[calc(50%-12px)]">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold mb-2 text-lg">Relaks i aktywności na świeżym powietrzu</h5>
                  <p className="text-stone-200 leading-relaxed">
                    Terraza Select z łóżkami balijskimi (dodatkowo płatne) oraz aktywności: minigolf, siatkówka plażowa, pétanque.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* B2B Info */}
          <div className="mt-16 text-center">
            <div className="bg-stone-100 rounded-xl p-8 inline-block">
              <h4 className="text-2xl font-semibold text-stone-800 mb-4">
                Kluczowy argument B2B
              </h4>
              <p className="text-lg text-stone-600 max-w-2xl leading-relaxed mb-4">
                Beach Club Laurel jest jedną z głównych atrakcji dla Twoich klientów szukających ekskluzywnych doświadczeń. 
                Podkreśl tę wyjątkową instalację w swoich materiałach sprzedażowych.
              </p>
              <p className="text-sm text-stone-500">
                Więcej informacji o day-pass: <a href="https://www.jardin-tecina.com/en/beach-club-laurel/day-pass/" target="_blank" rel="noopener noreferrer" className="underline hover:text-stone-700">jardin-tecina.com/beach-club-laurel/day-pass</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          <div className="max-w-6xl w-full">
            <img 
              src={images[selectedImage].url}
              alt={images[selectedImage].alt}
              className="w-full h-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Navigation arrows */}
          {selectedImage > 0 && (
            <button
              className="absolute left-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(selectedImage - 1);
              }}
            >
              <i className="ri-arrow-left-line text-2xl"></i>
            </button>
          )}
          
          {selectedImage < images.length - 1 && (
            <button
              className="absolute right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(selectedImage + 1);
              }}
            >
              <i className="ri-arrow-right-line text-2xl"></i>
            </button>
          )}
        </div>
      )}
    </>
  );
}
